import { memo, useEffect, useRef, useState } from "react";
import type { MediaProgress } from "@/react/types/MediaProgress";
import type { MediaTranscoding } from "@/react/types/MediaTranscoding";
import { usePunditUserContext } from "@circle-react/contexts";
import { isCommunityAdmin } from "@circle-react/helpers/communityMemberHelpers";
import { mediaTranscriptPaths } from "@circle-react/helpers/urlHelpers";
import { useMediaTranscoding } from "@circle-react/hooks/useMediaTranscoding";
import type { VideoVariants } from "@circle-react-shared/MediaPlayer/VideoPlayer/types";
import { MediaPlayer } from "@circle-react-uikit/MediaPlayer";
import { useMediaSettings } from "@circle-react-uikit/MediaSettings/useMediaSettings";
import { MediaSettings } from "../MediaSettings";
import { TranscodingOverlay } from "./TranscodingOverlay";
import { TranscriptNotification } from "./TranscriptNotification";
import { TranscriptPopup } from "./TranscriptPopup";
import { useMediaPlaybackStore } from "./store/useMediaPlaybackStore";
import { useMediaTranscript } from "./useMediaTranscript";
import { isMediaContentTypeNativelySupported } from "./utils";

export interface TranscriptsPlayerProps {
  chaptersUrl?: string;
  downloadUrl?: string;
  isDownloadable?: boolean;
  isTranscodingEnabled?: boolean;
  mediaTranscoding?: MediaTranscoding;
  mediaTranscriptId?: number;
  menuPosition?: "top" | "bottom";
  onProgress?: (progress: MediaProgress) => void;
  poster?: string;
  preload?: "none" | "metadata" | "auto";
  previewThumbnailsUrl?: string;
  signedId: string;
  src: string;
  type: string;
  variants?: VideoVariants;
}

interface PlayerRef {
  plyr: Plyr;
}

export const TranscriptsPlayerComponent = memo(
  ({
    src,
    poster,
    type,
    mediaTranscoding,
    mediaTranscriptId,
    isDownloadable = false,
    isTranscodingEnabled = false,
    downloadUrl,
    variants,
    preload = "none",
    signedId,
    onProgress,
    menuPosition = "top",
  }: TranscriptsPlayerProps) => {
    const { currentCommunityMember, currentCommunitySettings } =
      usePunditUserContext();
    const isAdmin = isCommunityAdmin(currentCommunityMember);
    const playerRef = useRef<PlayerRef | null>(null);
    const fullscreenPlayerRef = useRef<PlayerRef | null>(null);
    const [createdMediaTranscriptId, setCreatedMediaTranscriptId] = useState<
      number | null
    >(null);
    const hlsSrc = isTranscodingEnabled ? variants?.hls : null;
    const [playbackSrc, setPlaybackSrc] = useState(hlsSrc ?? src);
    const isMediaHls = playbackSrc.endsWith(".m3u8");
    const isMediaNativelySupported = isMediaContentTypeNativelySupported(type);
    const isMediaSupported = isMediaHls || isMediaNativelySupported;

    const storedMediaProgress = useMediaPlaybackStore(state =>
      state.getProgress(signedId || ""),
    );

    const deleteMediaProgress = useMediaPlaybackStore(
      state => state.deleteProgress,
    );

    const saveMediaProgress = useMediaPlaybackStore(
      state => state.saveProgress,
    );

    const onEnded = () => {
      if (signedId) {
        deleteMediaProgress(signedId);
      }
    };

    const [currentTime, setCurrentTime] = useState(storedMediaProgress);

    const currentMediaTranscriptId =
      mediaTranscriptId || createdMediaTranscriptId;

    const hasTranscripts =
      !!currentCommunitySettings?.community_ai_settings_enabled;

    const { mediaSettings } = useMediaSettings(signedId || "");
    const onSeek = (time: any) => {
      const playerElement = playerRef.current;
      if (!playerElement) {
        return;
      }
      playerElement.plyr.currentTime = time;
    };

    const onFullscreenSeek = (time: any) => {
      const playerElement = fullscreenPlayerRef.current;
      if (!playerElement) {
        return;
      }
      playerElement.plyr.currentTime = time;
    };

    const onTranscriptFullscreenToggle = () => {
      const playerElement = playerRef.current;
      if (!playerElement) {
        return;
      }
      playerElement.plyr.pause();
    };

    useEffect(() => {
      if (currentTime && signedId) {
        saveMediaProgress(signedId, currentTime);
      }
    }, [currentTime, signedId, saveMediaProgress]);

    const { status: transcodingStatus } = useMediaTranscoding({
      enabled: isTranscodingEnabled && !isMediaSupported,
      signedId,
      mediaTranscoding,
      onSuccess: ({ hlsUrl }) => hlsUrl && setPlaybackSrc(hlsUrl),
    });

    const {
      parsedWebvtt,
      mediaTranscript,
      canEdit,
      updateAsync,
      isUpdating,
      isLoading,
      refetch,
    } = useMediaTranscript(currentMediaTranscriptId);
    const cues = parsedWebvtt?.cues ?? [];
    const onMediaTranscriptUpdated = (id: number) => {
      if (currentMediaTranscriptId !== id) {
        setCreatedMediaTranscriptId(id);
      } else {
        void refetch();
      }
    };

    const videoPoster = mediaSettings?.poster_url ?? poster;

    const shouldDisplayTranscodingOverlay =
      isTranscodingEnabled &&
      !isMediaSupported &&
      transcodingStatus !== "succeeded";

    const shouldDisplayTranscript =
      hasTranscripts && mediaTranscript?.enabled && mediaTranscript?.is_ready;

    const shouldDisplayNotification =
      hasTranscripts && !mediaTranscriptId && currentCommunityMember?.is_admin;

    const id =
      typeof mediaTranscriptId === "number" ? String(mediaTranscriptId) : "";

    const captionsUrl = shouldDisplayTranscript
      ? mediaTranscriptPaths.showVtt({ id })
      : undefined;

    const renderFullscreenPlayer = () => (
      <MediaPlayer
        captionsUrl={captionsUrl}
        contentType={type}
        downloadUrl={downloadUrl ?? variants?.original}
        isDownloadable={isDownloadable || isAdmin}
        onTimeUpdate={setCurrentTime}
        playerRef={fullscreenPlayerRef}
        poster={videoPoster}
        src={playbackSrc}
        startTime={currentTime}
        preload={preload}
        onEnded={onEnded}
        onProgress={onProgress}
        menuPosition={menuPosition}
        signedId={signedId}
      />
    );

    return (
      <div className="group relative">
        {shouldDisplayTranscodingOverlay && (
          <TranscodingOverlay status={transcodingStatus} />
        )}
        <MediaPlayer
          captionsUrl={captionsUrl}
          contentType={type}
          downloadUrl={downloadUrl ?? variants?.original}
          isDownloadable={isDownloadable || isAdmin}
          onTimeUpdate={setCurrentTime}
          playerRef={playerRef}
          poster={videoPoster}
          src={playbackSrc}
          menuPosition={menuPosition}
          startTime={currentTime}
          preload={preload}
          onEnded={onEnded}
          onProgress={onProgress}
          signedId={signedId}
        />
        <MediaSettings
          signedId={signedId}
          mediaTranscript={mediaTranscript}
          canEditTranscript={canEdit}
          updateAsyncTranscript={updateAsync}
          isUpdatingTranscript={isUpdating}
          contentType={type}
        />
        {shouldDisplayTranscript && (
          <TranscriptPopup
            onSeek={onSeek}
            onFullscreenSeek={onFullscreenSeek}
            renderFullscreenPlayer={renderFullscreenPlayer}
            onFullscreenToggle={onTranscriptFullscreenToggle}
            cues={cues}
            currentTime={currentTime}
          />
        )}
        {shouldDisplayNotification && (
          <TranscriptNotification
            url={src}
            onNewMediaTranscript={setCreatedMediaTranscriptId}
            onMediaTranscriptUpdated={onMediaTranscriptUpdated}
            mediaTranscript={mediaTranscript}
            isLoading={isLoading}
          />
        )}
      </div>
    );
  },
);

TranscriptsPlayerComponent.displayName = "TranscriptsPlayerComponent";
